import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Box,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Flight as FlightIcon,
  SwapHoriz as SwapIcon,
  Add as AddIcon,
  Launch as LaunchIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../auth/useAuth';

// כתובת בסיס לממשק בחירת/החלפת מושבים (ניתן להגדיר בבילד דרך REACT_APP_SELECTOR_URL)
const SEAT_SELECTOR_BASE_URL = (process.env.REACT_APP_SELECTOR_URL || 'http://localhost:5173').replace(/\/$/, '');

const AircraftReplacementManagement = () => {
  const { api } = useAuth();
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [replacementData, setReplacementData] = useState('');

  useEffect(() => {
    fetchFlights();
  }, []);

  const fetchFlights = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/flight-seating/flights');
      
      if (response.data && response.data.success) {
        const flightsList = response.data.flights || [];
        const validFlights = flightsList.filter(f => f && f.flightId);
        setFlights(validFlights);
      } else {
        setError('לא נמצאו טיסות במערכת');
        setFlights([]);
      }
    } catch (error) {
      setError(`שגיאה בטעינת רשימת הטיסות: ${error.response?.data?.message || error.message}`);
      setFlights([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReplacement = (flight) => {
    setSelectedFlight(flight);
    setOpenDialog(true);
  };

  const handleOpenReplacementInterface = (originalFlight, replacementFlight) => {
    const originalId = originalFlight.flightId;
    const replacementId = replacementFlight.flightId;
    
    // פתיחת ממשק החלפת המטוס בחלון חדש
    const url = `${SEAT_SELECTOR_BASE_URL}/aircraft-replacement?original=${originalId}&replacement=${replacementId}`;
    window.open(url, '_blank', 'width=1400,height=900');
  };

  const handleSubmitReplacement = async () => {
    try {
      if (!replacementData.trim()) {
        setError('נא להזין נתוני מטוס מחליף');
        return;
      }

      let seatData;
      try {
        seatData = JSON.parse(replacementData);
      } catch (e) {
        setError('פורמט JSON לא תקין');
        return;
      }

      const response = await api.post('/flight-seating/init', {
        seatData: seatData,
        COMPANY: selectedFlight.company || 'DERECH',
        EXTERNAL_ORDER_NUMBER: `REPLACEMENT_${Date.now()}`,
        IS_REPLACEMENT: true
      });

      if (response.data && response.data.success) {
        setOpenDialog(false);
        setReplacementData('');
        setSelectedFlight(null);
        fetchFlights(); // רענון הרשימה
        
        // הודעת הצלחה
        alert(`מטוס מחליף נוצר בהצלחה!\nמזהה: ${response.data.flightId}`);
      }
    } catch (error) {
      setError(`שגיאה ביצירת מטוס מחליף: ${error.response?.data?.message || error.message}`);
    }
  };

  const getFlightPairs = () => {
    const pairs = [];
    
    // בדיקה שיש נתונים ושהם מערך
    if (!Array.isArray(flights) || flights.length === 0) {
      return pairs;
    }
    
    // סינון טיסות מקוריות (בלי _REPLACEMENT)
    const originalFlights = flights.filter(f => 
      f && f.flightId && !f.flightId.includes('_REPLACEMENT')
    );
    
    originalFlights.forEach(original => {
      // חיפוש מטוס מחליף
      const replacement = flights.find(f => 
        f && f.flightId && f.flightId === `${original.flightId}_REPLACEMENT`
      );
      
      pairs.push({
        original,
        replacement,
        hasReplacement: !!replacement
      });
    });
    
    return pairs;
  };

  const flightPairs = getFlightPairs();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>טוען נתוני טיסות...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
        <FlightIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        ניהול החלפת מטוסים
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}


      <Grid container spacing={3}>
        {flightPairs.map((pair, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card elevation={3}>
              <CardContent>
                {/* מטוס מקורי */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FlightIcon sx={{ mr: 1 }} />
                    מטוס מקורי
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>טיסה:</strong> {pair.original.flightNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>תאריך:</strong> {pair.original.flightDate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>חברת תעופה:</strong> {pair.original.airline}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>מושבים זמינים:</strong> {pair.original.availableSeats}/{pair.original.totalSeats}
                  </Typography>
                </Box>

                {/* מטוס מחליף */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  {pair.hasReplacement ? (
                    <>
                      <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SwapIcon sx={{ mr: 1, color: 'success.main' }} />
                        מטוס מחליף קיים
                        <Chip label="פעיל" color="success" size="small" sx={{ ml: 1 }} />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>מושבים זמינים:</strong> {pair.replacement.availableSeats}/{pair.replacement.totalSeats}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>נוצר:</strong> {new Date(pair.replacement.createdAt || Date.now()).toLocaleDateString('he-IL')}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="subtitle1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      אין מטוס מחליף
                    </Typography>
                  )}
                </Box>

                {/* פעולות */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {!pair.hasReplacement ? (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => handleCreateReplacement(pair.original)}
                      size="small"
                    >
                      צור מטוס מחליף
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<SwapIcon />}
                        onClick={() => handleOpenReplacementInterface(pair.original, pair.replacement)}
                        size="small"
                      >
                        החלף מושבים
                      </Button>
                      <Tooltip title="פתח בחלון חדש">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenReplacementInterface(pair.original, pair.replacement)}
                          size="small"
                        >
                          <LaunchIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  
                  <Tooltip title="מידע נוסף">
                    <IconButton color="info" size="small">
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {flightPairs.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <Typography variant="h6" color="text.secondary">
            {flights.length === 0 ? 'אין טיסות במערכת' : 'אין טיסות מקוריות במערכת'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {flights.length === 0 
              ? 'צור טיסות באמצעות ה-API כדי להתחיל לנהל החלפות מטוסים'
              : `נמצאו ${flights.length} טיסות, אבל כולן מטוסים מחליפים`
            }
          </Typography>
          {flights.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontFamily: 'monospace' }}>
              טיסות שנמצאו: {flights.map(f => f.flightId).join(', ')}
            </Typography>
          )}
        </Paper>
      )}

      {/* דיאלוג יצירת מטוס מחליף */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          יצירת מטוס מחליף עבור טיסה {selectedFlight?.flightNumber}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            הזן נתוני מושבים בפורמט JSON עבור המטוס המחליף:
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={12}
            variant="outlined"
            label="נתוני מושבים (JSON)"
            value={replacementData}
            onChange={(e) => setReplacementData(e.target.value)}
            placeholder={`[
  {
    "MISPAR_NESIA_DERECH": "123456",
    "MISPAR_NESIA_NETIVIM": "654321",
    "TAARICH_TISA": "${selectedFlight?.flightDate}",
    "MISPAR_TISA": "${selectedFlight?.flightNumber}",
    "CHEVRAT_TEUFA": "${selectedFlight?.airline}",
    "KIVUN_TISA": "H",
    "DESC_TOSAFOT_TISA": "1A",
    "C_CODE_TOSAFOT_TISA_TYPE": "18",
    "TOSAFOT_TISA_TYPE_DESC": "שורה ראשונה",
    "SCHUM": "50.00",
    "IND_SHOW": "Y",
    "U_ALUT_DERECH": "ALT001",
    "U_ALUT_NETIVIM": "ALT002"
  }
]`}
            sx={{ fontFamily: 'monospace' }}
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>שימו לב:</strong> המטוס המחליף יקבל את אותו מזהה טיסה עם סיומת "_REPLACEMENT"
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            ביטול
          </Button>
          <Button 
            onClick={handleSubmitReplacement}
            variant="contained"
            color="primary"
          >
            צור מטוס מחליף
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AircraftReplacementManagement;
